import { Session, SessionDocument, UnsubmittedSession } from '../components/sessions/session.model';
import { IPolicy } from '../state/policy/policy.reducer';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { Store } from '@ngrx/store';
import { ISiteInformation } from '../state/configuration/site-information.reducer';
import { IBiographic, IBiographicPatch } from '../components/biographics/biographics.model';
import { each } from 'lodash-es';
import { concatMap, delay, map, reduce, retryWhen, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { ApiGatewayService } from '@drc-eca/eca-components-lib';
import { ApiGatewayClientService, EnvironmentConfigurationService } from '@drc-eca/eca-lrc-common-ui';
import { MasterForm } from '../components/alerts/review/master-forms.model';
import { Pending, PendingResponse, PendingWithAlerts } from '../components/biographics/pending.model';
import { IS3Policy, IScanBatch, IScans } from '../state/interfaces';

@Injectable({
  providedIn: 'root',
})
export class LocalScanningWebApiService extends ApiGatewayClientService {
  protected http = inject(HttpClient);
  protected store = inject<Store<any>>(Store);
  protected apiGatewayService: ApiGatewayService;
  protected environmentConfigurationService: EnvironmentConfigurationService;


  private siteInformation$: Observable<ISiteInformation>;

  private policy$: Observable<IPolicy>;

  private scans$: Observable<IScans>;

  sendScansAttemptCount = 3;

  /* istanbul ignore next */
  constructor() {
    const apiGatewayService = inject(ApiGatewayService);
    const environmentConfigurationService = inject(EnvironmentConfigurationService);

    super(apiGatewayService, environmentConfigurationService, 'localScanningApiUrl');
    this.apiGatewayService = apiGatewayService;
    this.environmentConfigurationService = environmentConfigurationService;

    this.siteInformation$ = this.store.select('siteInformation');
    this.policy$ = this.store.select('policy');
    this.scans$ = this.store.select('scans');
  }

  getBiographic(sessionId: string, sortKey: string): Observable<IBiographic> {
    return this.get<IBiographic>(`sessions/${sessionId}/biographics/${sortKey}`);
  }

  getBiographics(sessionId: string): Observable<IBiographic[]> {
    return this.get<IBiographic[]>(`sessions/${sessionId}/biographics`)
      .pipe(
        map(biographics => {
          return biographics.map(bio => {
            bio.sessionId = sessionId;
            return bio;
          });
        }),
      );
  }

  /* No param is needed because userId is sent in JWT token */
  getSessionDocuments(): Observable<SessionDocument[]> {
    return this.get<SessionDocument[]>(`sessions`);
  }

  getSessions(): Observable<Session[]> {
    return this.getSessionDocuments()
      .pipe(
        map(docs => Session.getCollection(docs))
      );
  }

  getPending(): Observable<Pending[]> {
    console.log(`getPending() being called`);
    return this.get<PendingResponse[]>(`pending`).pipe(
      map(r => r.map(pending => new Pending(pending))),
    );
  }

  getUnsubmittedSessions(): Observable<UnsubmittedSession[]> {
    return this.get(`sessions/unsubmitted`);
  }

  patchPending(pendingId: string, body: IBiographicPatch) {
    return this.patch<IBiographic>(`pending/${pendingId}`, body);
  }

  /* TODO (CN) remove this and all scoring events in scanner section  */

  /* istanbul ignore next */
  submitToScoring(sessionId: string) {
    return this.post(`sessions/${sessionId}/score`, {});
  }

  submitDocumentsToScoring(pendings: Pending[]) {
    return this.post(`pending/score`, pendings.map(pending => pending.lithocode));
  }

  getScansBySessionId(sessionId: string, populateAllFields = false) {
    return this.get(`scans?sessionId=${sessionId}&populateAllFields=${populateAllFields}`);
  }

  getS3UploadPolicy(s3Key: string): Observable<IS3Policy> {
    return this.get(`scans/policy?s3Key=${s3Key}`);
  }

  getScanMetadataPolicy() {
    return this.siteInformation$.pipe(
      switchMap(siteInfo => {
        return this.post(`scans/metadata/policy`, {
          path: siteInfo.path,
        }, this.options);
      })
    );
  }

  /* Todo clear */
  clearAlerts(pending: { lithocode: string }[]): Observable<any> {
    return this.post(`pending/clear-alerts`, pending.map(alert => alert.lithocode));
  }

  clearAlertFor(pending: PendingWithAlerts | { lithocode: string }): Observable<any> {
    return this.clearAlerts([pending]);
  }

  getResultsBySessionId(sessionId: string, populateAllFields = false): Observable<any> {
    return this.get(`results/${sessionId}?populateAllFields=${populateAllFields}`, this.options);
  }

  uploadImages(scanBatch: IScanBatch) {
    let currentImageNumber = 1;
    const ids = Object.keys(scanBatch);

    return from(ids)
      .pipe(
        withLatestFrom(
          this.siteInformation$),
        concatMap(
          ([id, siteInformation]) => {
            const s3Key = `${siteInformation.client}/scans/${id}`;
            console.log(`Uploading image #${currentImageNumber++}, size: ${scanBatch[id].blob.size}`);

            return this.getS3UploadPolicy(s3Key).pipe(
              switchMap((s3Policy) => {
                const s3FormData: FormData = new FormData();

                // backward compatibility with form-rec-service. Key will already be set on policy from local-scanning-service
                delete s3Policy.fields.key;
                s3FormData.append('key', s3Key);

                each(s3Policy.fields, (value, key) => {
                  s3FormData.append(key, value);
                });
                // NOTE: Make sure the file is appended last or s3 will 403 the request
                s3FormData.append('file', scanBatch[id].blob, `${id}`);
                return this.http.post(s3Policy.url, s3FormData);
              })
            );
          }),
        retryWhen(errors => {
          /* istanbul ignore next */
          return errors.pipe(
            delay(2000),
            take(this.sendScansAttemptCount),
            concatMap(error => throwError(error)),
          );
        }),
        reduce(() => scanBatch)
      );
  }

  uploadMetadata(scanBatch: IScanBatch) {
    const ids = Object.keys(scanBatch);

    return from(ids)
      .pipe(
        withLatestFrom(
          this.siteInformation$,
          this.policy$
        ),
        concatMap(([id, siteInformation, policy]) => {
          const scan = scanBatch[id];
          return this.post(`scans`, {
            id: scan.id,
            backsideId: scan.backsideId,
            sessionId: scan.sessionId,
            client: siteInformation.client,
            policy: policy.apiPolicy,
            formStatus: scan.formStatus,
          }, this.options)
            .pipe(
              map(response => ({
                id,
                response,
              }))
            );

        }),
        retryWhen(errors => {
          /* istanbul ignore next */
          return errors.pipe(
            delay(2000),
            take(this.sendScansAttemptCount),
            concatMap(error => throwError(`Multiple attempts to send images to the server failed (${error.message})`)),
          );
        }),
      );
  }

  sendScans(scanBatch: IScanBatch) {
    // synchronously execute multiple http requests with concatMap,
    // operating on the ids of the object we want to post
    return this.uploadImages(scanBatch)
      .pipe(
        concatMap(() => this.uploadMetadata(scanBatch))
      );
  }

  reprocess({scanIds, sessionId, debug}) {
    return of(scanIds)
      .pipe(
        switchMap((ids) => {
          return this.post(`diagnostics/reprocess`, {
            scanIds: ids,
            sessionId,
            debug,
          });
        }),
        retryWhen(errors => {
          /* istanbul ignore next */
          return errors.pipe(
            delay(2000),
            take(this.sendScansAttemptCount),
            concatMap(error => throwError(error)),
          );
        }),
      );
  }

  getMasterForm(materialId: string, pending: Pending = null): Observable<MasterForm> {
    // existing pending records do not have formEditVersion so there is an alternate call if it exists
    if (Number.isInteger(pending?.formEditVersion)) {
      return this.scans$.pipe(
        switchMap(() => {
          return this.get<MasterForm>(`forms/materialId/${materialId}/version/${pending.formEditVersion}`, this.options);
        })
      );
    }
    return this.scans$.pipe(
      switchMap(() => {
        const formStatus = pending?.formStatus ? pending.formStatus : 'Published';
        return this.get<MasterForm[]>(`forms/materialId/${materialId}/status/${formStatus}`, this.options);
      }),
      map((forms: MasterForm[]) => {
        // Note we should get either 0 or 1 master forms that have either a status of Draft or Published from the forms endpoint.
        // We expect exactly 1, but this is a defensive check.
        return forms && forms.length > 0 ? forms[0] : null;
      }),
    );

  }

  getScanImageUrl(imageId: string): Observable<string> {
    return this.get(`scans/signed-url/download`, {
      httpOptions: {
        params: new HttpParams({fromObject: {imageId}}),
      }
    });
  }

  // TODO : this is currently pointing to a broken lambda function that was commented out when we cut over from
  //  form-rec-svc to local-scanning-svc.
  getDebugImageSignedUrls(debugScanKey: string): Observable<any> {
    return this.get(`diagnostics/debug`, {
      httpOptions: {
        params: new HttpParams({fromObject: {debugScanKey}}),
      }
    });
  }
}



